import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from 'api/config';
import { useMutationHTTPRequest } from 'api/hooks/useHTTPRequest';
import { logError } from 'lib/sentry/logError';

import { Messages } from 'features/chat/config/types/Message';
import { User } from 'features/user/User';

import { showErrorToast } from 'shared/components/Toast';

export const useCreateChatMessageReaction = ({
  chatId,
  user,
}: {
  chatId: string;
  user: User;
}) => {
  const { request } = useMutationHTTPRequest();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ reaction, messageId }: MutationFnPropType) =>
      await request({
        url: `/api/chats/${chatId}/messages/${messageId}/reactions/create`,
        body: { reaction },
      }),
    {
      onMutate: async ({ reaction, messageId }: MutationFnPropType) => {
        await queryClient.cancelQueries({
          queryKey: [queryKeys.chatMessagesHistory, chatId],
          exact: false,
        });

        const previousMessagesMap = new Map();
        queryClient
          .getQueryCache()
          .findAll({
            queryKey: [queryKeys.chatMessagesHistory, chatId],
            exact: false,
          })
          .forEach((query) => {
            previousMessagesMap.set(
              JSON.stringify(query.queryKey),
              query.state.data
            );
          });

        queryClient
          .getQueryCache()
          .findAll({
            queryKey: [queryKeys.chatMessagesHistory, chatId],
            exact: false,
          })
          .forEach((query) => {
            queryClient.setQueryData<{ messages: Messages }>(
              query.queryKey,
              (old) => {
                if (!old?.messages) return old;

                return {
                  ...old,
                  messages: old.messages.map((message) => {
                    if (message.messageId === messageId) {
                      return {
                        ...message,
                        reactions: {
                          ...message.reactions,
                          [reaction]: [
                            ...(message.reactions?.[reaction] || []),
                            {
                              email: user.email,
                              firstName: user.firstName,
                              lastName: user.lastName,
                              name: user.name,
                              picture: user.picture,
                            },
                          ],
                        },
                      };
                    }
                    return message;
                  }),
                };
              }
            );
          });

        return { previousMessagesMap };
      },
      onError: (err, { messageId }, context) => {
        showErrorToast(
          "There's an error with adding reaction. Please try again later."
        );

        if (context?.previousMessagesMap) {
          context.previousMessagesMap.forEach((data, key) => {
            queryClient.setQueryData(JSON.parse(key), data);
          });
        }

        logError(err);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.chatMessagesHistory, chatId],
          exact: false,
        });
      },
    }
  );
};

type MutationFnPropType = { reaction: string; messageId: string };
